import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Badge from '../components/common/Badge';
import Loader from '../components/common/Loader';
import { getFeaturedJobs } from '../api';

const Browse = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('All');

  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        const res = await getFeaturedJobs();
        setJobs(res.data);
      } catch (err) {
        console.error('Failed to fetch jobs', err);
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          job.company_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'All' || job.employment_type === filterType;
    return matchesSearch && matchesType;
  });

  return (
    <div className="page-wrapper">
      <Navbar />
      
      <main className="main-content bg-gray-50 pb-20">
        <div className="bg-white border-b border-gray-200 pt-12 pb-16">
          <div className="container-sm text-center">
            <h1 className="heading-1 mb-4">Find your next opportunity</h1>
            <p className="subheading mb-8">Discover internships and entry-level roles at top companies.</p>
            
            <div className="search-bar shadow-sm">
              <span className="search-icon">🔍</span>
              <input 
                type="text" 
                placeholder="Search by job title, company, or keywords..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
              <Button>Search</Button>
            </div>
          </div>
        </div>

        <div className="container mt-12">
          <div className="flex gap-8">
            {/* Filters Sidebar */}
            <div className="w-1/4 hidden md:block">
              <div className="card">
                <h3 className="heading-3 mb-6">Filters</h3>
                
                <div className="mb-6">
                  <h4 className="form-label mb-3">Employment Type</h4>
                  <div className="flex-col gap-2">
                    {['All', 'Full Time', 'Part Time', 'Internship', 'Contract', 'Remote'].map(type => (
                      <label key={type} className="flex items-center gap-2 cursor-pointer">
                        <input 
                          type="radio" 
                          name="type" 
                          value={type}
                          checked={filterType === type}
                          onChange={e => setFilterType(e.target.value)}
                          className="accent-primary"
                        />
                        <span className="text-sm text-gray-700">{type}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Job Listings */}
            <div className="flex-1">
              <div className="flex justify-between items-center mb-6">
                <h2 className="heading-3">Showing {filteredJobs.length} jobs</h2>
              </div>

              {loading ? (
                <div className="flex justify-center py-20"><Loader /></div>
              ) : filteredJobs.length === 0 ? (
                <div className="empty-state card">
                  <div className="empty-icon">🔍</div>
                  <h3 className="empty-title">No jobs found</h3>
                  <p className="empty-desc">We couldn't find any opportunities matching your criteria. Try adjusting your filters or search terms.</p>
                </div>
              ) : (
                <div className="flex-col gap-4">
                  {filteredJobs.map(job => (
                    <Link to={`/opportunity/${job.id}`} key={job.id} className="job-card group">
                      <div className="job-card-header">
                        <div className="flex gap-4">
                          <div className="job-company-logo">
                            {job.company_name?.charAt(0) || '🏢'}
                          </div>
                          <div>
                            <h3 className="job-title group-hover:text-primary transition-colors">{job.title}</h3>
                            <div className="job-company">{job.company_name}</div>
                          </div>
                        </div>
                        <Badge variant="primary-light">{job.employment_type || 'Job'}</Badge>
                      </div>
                      
                      <div className="job-meta mt-2">
                        <div className="job-meta-item">📍 {job.location || 'Remote'}</div>
                        {job.salary && <div className="job-meta-item">💰 {job.salary}</div>}
                        <div className="job-meta-item">🕒 Apply by {new Date(job.deadline).toLocaleDateString()}</div>
                      </div>
                      
                      <div className="job-footer mt-4">
                        <div className="flex gap-2">
                          {job.skills?.split(',').slice(0, 3).map((skill, i) => (
                            <Badge key={i} variant="gray">{skill.trim()}</Badge>
                          ))}
                          {job.skills?.split(',').length > 3 && <Badge variant="gray">+{job.skills.split(',').length - 3}</Badge>}
                        </div>
                        <Button variant="outline" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">View Details</Button>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Browse;
